/**
 * AdjustmentBulkDecideDialog — një vendim mbi shumë rregullime.
 *
 * Radha e shqyrtimit të një muaji ka dhjetëra rreshta të njëjtë (bonusi i
 * ekipit, kthimet e avancit), dhe një panel për secilin do të thoshte dhjetëra
 * hapje e mbyllje — pra, në praktikë, aprovim pa shikuar.
 *
 * Kushti i shtëpisë mbetet: arsyetimi rri PARA vendimit. Ndaj ky dialog nuk është
 * "aprovo 12 rreshta", por lista e plotë e tyre — kush, sa, për kur dhe pse —
 * bashkë me shumën që hyn në pagë. Vendimi konfirmohet mbi atë listë, jo mbi një
 * numër.
 *
 * Serveri i kalon rreshtat një nga një nëpër të njëjtin verifikim si veç e veç
 * (`POST /api/payroll-adjustments/decide`): grupi nuk është shteg anash. Ato që
 * nuk kalojnë kthehen me arsyen e vet dhe shfaqen këtu — pa refuzime të heshtura.
 */
import React, { useMemo, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Stack, TextField, Typography, alpha, useTheme,
} from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CancelRoundedIcon      from '@mui/icons-material/CancelRounded';
import { authFetch } from '../../services/apiServices';
import { MONTHS_SQ } from '../../utils/period';
import { CATEGORY_META, WORKFLOW, canRun, reasonOk, typeLabel, MIN_REASON } from '../../config/payrollAdjustments';

const fmt = (n) => Number(n || 0).toLocaleString('sq-AL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const periodOf = (r) => (r.periodYear && r.periodMonth ? `${MONTHS_SQ[r.periodMonth - 1]} ${r.periodYear}` : '—');

export default function AdjustmentBulkDecideDialog({
  open, action, rows = [], actor = {}, onClose, onDone,
}) {
  const theme = useTheme();
  const [reason, setReason] = useState('');
  const [busy,   setBusy]   = useState(false);
  const [error,  setError]   = useState('');
  const [result, setResult] = useState(null);

  const def = action ? WORKFLOW[action] : null;

  // Rreshtat që veprimi nuk i pranon nuk dërgohen fare: më mirë t'i shohë
  // përdoruesi para se t'i numërojë serveri si "të anashkaluar".
  const eligible = useMemo(() => rows.filter((r) => canRun(action, r, actor)), [rows, action, actor]);
  const ineligible = rows.length - eligible.length;

  const total = useMemo(() => eligible.reduce((sum, r) => {
    const amt = Number(r.amount) || 0;
    if (r.category === 'reimbursement') return sum;             // paguhet jashtë pagës
    return sum + (r.category === 'addition' ? amt : -amt);
  }, 0), [eligible]);

  const blocked = Boolean(def?.reason) && !reasonOk(reason);

  const run = async () => {
    if (!def || !eligible.length || blocked) return;
    setBusy(true); setError('');
    try {
      const r = await authFetch('/api/payroll-adjustments/decide', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, ids: eligible.map((x) => x.id), reason: reason.trim() }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.errorMessage || 'Veprimi dështoi.');
      // Kur ndonjë rresht nuk kaloi, dialogu MBETET hapur me arsyet: një
      // "u aprovuan 9 nga 12" që zhduket vetë nuk thotë cilët tre mbetën.
      if (j.data?.skipped?.length) setResult(j.data);
      else onDone?.(action, j.data);
    } catch (e) {
      setError(e.message);
    } finally { setBusy(false); }
  };

  const close = () => {
    setReason(''); setError(''); setResult(null);
    onClose?.();
  };

  if (!def) return null;

  return (
    <Dialog open={open} onClose={() => !busy && close()} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ fontWeight: 800, fontSize: '1rem' }}>
        {def.label} — {eligible.length} {eligible.length === 1 ? 'rregullim' : 'rregullime'}
      </DialogTitle>

      <DialogContent dividers sx={{ px: 2.5 }}>
        {result ? (
          <>
            <Alert severity={result.done ? 'warning' : 'error'} sx={{ borderRadius: 2, mb: 1.5 }}>
              U kaluan <strong>{result.done}</strong> nga {result.done + result.skipped.length}.
              Këta mbetën pa vendim:
            </Alert>
            <Stack spacing={0.75}>
              {result.skipped.map((s) => (
                <Box key={s.id} sx={{ p: 1, borderRadius: 1.5, bgcolor: '#FEF2F2', border: '1px solid #FECACA' }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#991B1B' }}>
                    #{s.id} — {rows.find((r) => String(r.id) === String(s.id))?.workerName || 'rregullim'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.74rem', color: '#7F1D1D' }}>{s.error}</Typography>
                </Box>
              ))}
            </Stack>
          </>
        ) : (
          <>
            <Typography sx={{ fontSize: '0.82rem', color: '#4B5563', mb: 1.5 }}>{def.hint}</Typography>

            {ineligible > 0 && (
              <Alert severity="info" sx={{ borderRadius: 2, mb: 1.5, fontSize: '0.8rem' }}>
                {ineligible} nga rreshtat e zgjedhur nuk e pranojnë këtë veprim (status i papërshtatshëm,
                ose është rregullimi i vetvetes) — janë lënë jashtë.
              </Alert>
            )}

            {/* Lista e plotë, me arsyetimin e secilit: kushti i shtëpisë është që
                vendimi të merret mbi çka shkruan kërkesa, jo mbi numrin e tyre. */}
            <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #E5E7EB', borderRadius: 2 }}>
              {eligible.map((r, i) => {
                const m = CATEGORY_META[r.category] || {};
                return (
                  <Box key={r.id} sx={{
                    px: 1.5, py: 1.1, borderLeft: `3px solid ${m.bg}`,
                    borderTop: i ? '1px solid #F3F4F6' : 'none',
                  }}>
                    <Stack direction="row" justifyContent="space-between" spacing={1.5} alignItems="baseline">
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1F2937' }}>
                        {r.workerName || 'Punëtor i fshirë'}
                      </Typography>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: m.color, whiteSpace: 'nowrap' }}>
                        {m.sign}€{fmt(r.amount)}
                      </Typography>
                    </Stack>
                    <Typography sx={{ fontSize: '0.68rem', color: '#9CA3AF' }}>
                      {typeLabel(r.category, r.type)} · {periodOf(r)}
                    </Typography>
                    <Typography sx={{
                      fontSize: '0.75rem', mt: 0.25,
                      color: r.description ? '#4B5563' : '#9CA3AF',
                      fontStyle: r.description ? 'normal' : 'italic',
                    }}>
                      {r.description || 'Pa arsyetim të dhënë'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>

            <Stack direction="row" justifyContent="space-between" sx={{
              mt: 1.5, px: 1.5, py: 1, borderRadius: 2,
              bgcolor: alpha(theme.palette[def.tone].main, 0.07),
            }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#4B5563' }}>
                Efekti neto në pagë
              </Typography>
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, color: total < 0 ? '#C2410C' : '#15803D' }}>
                {total >= 0 ? '+' : '−'}€{fmt(Math.abs(total))}
              </Typography>
            </Stack>

            <TextField
              size="small" fullWidth multiline rows={2} sx={{ mt: 1.5 }}
              label={def.reason ? 'Arsyeja e refuzimit' : 'Shënim (opsional)'}
              required={def.reason}
              value={reason} onChange={(e) => setReason(e.target.value)}
              helperText={def.reason
                ? `E njëjta arsye shkon te secili punonjës · së paku ${MIN_REASON} shenja`
                : 'Ruhet në kronologjinë e secilit rresht'}
            />
            {error && <Alert severity="error" sx={{ mt: 1.5, borderRadius: 2 }}>{error}</Alert>}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={close} disabled={busy} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {result ? 'Mbyll' : 'Anulo'}
        </Button>
        {!result && (
          <Button variant="contained" disableElevation color={def.tone} onClick={run}
            disabled={busy || blocked || !eligible.length}
            startIcon={busy ? <CircularProgress size={15} color="inherit" />
              : action === 'reject' ? <CancelRoundedIcon sx={{ fontSize: 17 }} /> : <CheckCircleRoundedIcon sx={{ fontSize: 17 }} />}
            sx={{ textTransform: 'none', fontWeight: 700 }}>
            {busy ? 'Duke ruajtur…' : `Konfirmo — ${eligible.length}`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
